"use client";

import {
	BarChart2,
	BookOpen,
	Briefcase,
	Building2,
	ChevronRight,
	Clapperboard,
	Code2,
	GraduationCap,
	Headphones,
	Heart,
	Home,
	type LucideIcon,
	Megaphone,
	Package,
	Paintbrush,
	PenLine,
	Search,
	Server,
	Settings2,
	Share2,
	Sparkles,
	TrendingUp,
	Users,
	Wallet,
} from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";

// Keyed by category slug. Lives here (client component) rather than in the
// server page — the actual icon component reference (a lucide-react
// forwardRef) can never cross the server->client RSC boundary as a prop, so
// the page passes the serializable `slug` string and this map resolves it
// locally. See LOKERDOLLARCOM-P1/P2/P3.
const CATEGORY_ICONS: Record<string, LucideIcon> = {
	engineering: Code2,
	design: Paintbrush,
	product: Package,
	marketing: Megaphone,
	ai: Sparkles,
	"data-analytics": BarChart2,
	"video-media": Clapperboard,
	"writing-content": PenLine,
	operations: Settings2,
	"customer-support": Headphones,
	finance: Wallet,
	"hr-recruiting": Users,
	legal: Briefcase,
	education: GraduationCap,
	healthcare: Heart,
	"real-estate": Home,
	sales: TrendingUp,
	"social-media": Share2,
	"project-management": BookOpen,
	"ux-research": Search,
	devops: Server,
	companies: Building2,
};

interface CategoryCardProps {
	href: string;
	/** Category slug (e.g. "engineering") — the `category` prop on the
	 * `category_card_clicked` event, and the key into `CATEGORY_ICONS`. */
	slug: string;
	name: string;
	locale: string;
	ariaLabel: string;
}

/**
 * Card/link for a single entry in the /categories grid. Client component so
 * it can fire `category_card_clicked` on click; visually identical to the
 * inline `<Link>` it replaces.
 */
export function CategoryCard({
	href,
	slug,
	name,
	locale,
	ariaLabel,
}: CategoryCardProps) {
	const posthog = usePostHog();
	const Icon = CATEGORY_ICONS[slug] ?? Briefcase;
	return (
		<Link
			href={href}
			onClick={() =>
				posthog?.capture("category_card_clicked", { category: slug, locale })
			}
			className="group flex items-center justify-between rounded-xl border border-border bg-background px-4 py-3.5 text-[14px] font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer"
			aria-label={ariaLabel}
		>
			<span className="flex items-center gap-2">
				<Icon
					aria-hidden="true"
					className="w-4 h-4 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity"
				/>
				{name}
			</span>
			<ChevronRight
				aria-hidden="true"
				className="w-3.5 h-3.5 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity"
			/>
		</Link>
	);
}
